import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";

// Curated, buyer-intent evergreen guides (general, city="") that help pick a bouquet.
// Image-first order so the default four cards all have covers; text guides act as fallback.
const CURATED_SLUGS = [
  "cvety-po-povodam",
  "skolko-roz-darit",
  "rozy-ili-piony-chto-vybrat",
  "pochemu-rozy-vyanut",
  "kak-vybrat-buket",
  "znachenie-cvetov",
];

export const RelatedGuidesSection = ({ lang = "ru" }) => {
  const [guides, setGuides] = useState([]);

  useEffect(() => {
    const fetchGuides = async () => {
      try {
        const res = await fetch(`/api/blog/posts?city=&limit=30`);
        const data = await res.json();
        const bySlug = {};
        (data.articles || []).forEach((a) => {
          bySlug[a.slug] = a;
        });
        const ordered = CURATED_SLUGS.map((s) => bySlug[s]).filter(Boolean).slice(0, 4);
        setGuides(ordered);
      } catch {
        // silently fail
      }
    };
    fetchGuides();
  }, []);

  if (guides.length === 0) return null;

  const langSuffix = lang !== "ru" ? `?lang=${lang}` : "";
  const titleMain = lang === "tr" ? "Buket seçmenize yardımcı rehberler" : (lang === "en" ? "Guides to Help You Choose" : "Похожие гиды — помогут выбрать букет");
  const subtitle = lang === "tr" ? "Hangi çiçekleri ve kaç adet vereceğinizi öğrenin" : (lang === "en" ? "Which flowers to give, how many, and for what occasion" : "Какие цветы дарить, сколько и по какому поводу");
  const readMore = lang === "tr" ? "Oku" : (lang === "en" ? "Read" : "Читать");

  return (
    <section className="py-12 sm:py-16 bg-white" data-testid="related-guides-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="mb-6 sm:mb-8">
          <h2
            className="text-2xl sm:text-3xl font-bold text-gray-900"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            <Sparkles className="w-6 h-6 text-pink-500 inline-block mr-2 -mt-1" />
            {titleMain}
          </h2>
          <p className="text-sm text-gray-500 mt-2">{subtitle}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {guides.map((guide) => {
            const title = lang === "tr" && guide.title_tr ? guide.title_tr : (lang === "en" && guide.title_en ? guide.title_en : guide.title_ru);
            const excerpt = lang === "tr" && guide.excerpt_tr ? guide.excerpt_tr : (lang === "en" && guide.excerpt_en ? guide.excerpt_en : guide.excerpt_ru);
            return (
              <Link
                key={guide.slug}
                to={`/blog/${guide.slug}${langSuffix}`}
                className="group flex flex-col rounded-2xl overflow-hidden border border-gray-100 hover:border-pink-200 hover:shadow-lg transition-all bg-white"
                data-testid={`guide-card-${guide.slug}`}
              >
                {guide.cover_image ? (
                  <div className="relative h-32 sm:h-36 overflow-hidden">
                    <img
                      src={guide.cover_image}
                      alt={title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                ) : (
                  <div className="relative h-32 sm:h-36 overflow-hidden bg-gradient-to-br from-pink-100 via-rose-50 to-pink-200 flex items-center justify-center">
                    <Sparkles className="w-8 h-8 text-pink-300" />
                  </div>
                )}
                <div className="flex flex-col flex-1 p-4 sm:p-5">
                  <h3 className="text-sm font-bold text-gray-900 mb-2 group-hover:text-pink-600 transition-colors line-clamp-2">
                    {title}
                  </h3>
                  <p className="text-xs text-gray-500 line-clamp-3 mb-3 flex-1">{excerpt}</p>
                  <span className="text-xs font-semibold text-pink-500 group-hover:text-pink-600 flex items-center gap-1">
                    {readMore} <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default RelatedGuidesSection;
